import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AirplanemodeActiveIcon from "@mui/icons-material/AirplanemodeActive";
import FlightIcon from "@mui/icons-material/Flight";
import HistoryIcon from "@mui/icons-material/History";
import InfoIcon from "@mui/icons-material/Info";
import SpeedIcon from "@mui/icons-material/Speed";
import {
  Box,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { EmptyState } from "../../components/EmptyState";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useHangarsState } from "../../containers/HangarsStateContainer";
import { useIdentity } from "../../containers/IdentityContainer";
import { usePermissions } from "../../hooks/usePermissions";
import { getStackPolygons } from "../../hooks/utils";
import { FBO, Hangar, Preference, Tenant } from "../../types";
import { formatCurrency, scrollSx } from "../../utils";
import { sum } from "../../utils/lodash-replacements";
import { getRoleSetting, Permission } from "../../utils/permissions";
import { HangarIcon } from "../../widgets/Icons/HangarIcon";
import {
  calculateTotalArea,
  calculateUtilization,
  findTenantsFullyInHangar,
} from "../Hangar/HangarInfoCard";
import { HangarRow } from "./components/HangarRow";
import { StatCard } from "./components/StatCard";
import { HangarsSkeleton } from "./HangarsSkeleton";
import { MultiHangarButton } from "./MultiHangarSelector";

export const getNumberFromFirstOrLastWord = (
  text: string
): [number, string] => {
  // get first integer from text via regex
  const matches = text.match(/\d+/);
  if (matches) {
    return [Number(matches[0]), text.match(/\d+[A-Za-z]+/)?.[0]];
  }
  return [null, null];
};

export const sortHangars = (
  a: Hangar,
  b: Hangar,
  hangarDisplayOrder?: string[]
): number => {
  if (hangarDisplayOrder) {
    const aIndex = hangarDisplayOrder.indexOf(a.id);
    const bIndex = hangarDisplayOrder.indexOf(b.id);
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    } else if (aIndex !== -1) {
      return -1;
    } else if (bIndex !== -1) {
      return 1;
    }
  }
  return sortByNumberThenText(a.name, b.name);
};

export const sortByNumberThenText = (a: string, b: string): number => {
  const [aNumber, aAlphaNumeric] = getNumberFromFirstOrLastWord(a);
  const [bNumber, bAlphaNumeric] = getNumberFromFirstOrLastWord(b);

  if (aNumber !== null && bNumber !== null) {
    // If both first words are numbers, compare them numerically
    if (Number(aNumber) === Number(bNumber)) {
      if (aAlphaNumeric && bAlphaNumeric) {
        return aAlphaNumeric.localeCompare(bAlphaNumeric);
      }
      return a.localeCompare(b);
    }
    return Number(aNumber) - Number(bNumber);
  } else if (aNumber !== null) {
    // If only the first word in a is a number, a comes before b
    return -1;
  } else if (bNumber !== null) {
    // If only the first word in b is a number, b comes before a
    return 1;
  } else {
    // Otherwise, compare alphabetically
    return a.localeCompare(b);
  }
};

type Props = {};

export const estimateValue = (
  fbo: FBO,
  hangar: Hangar,
  polygons: any,
  _ignoreDeadSpaceForUtilization: boolean = false,
  monthly: boolean = false
): string => {
  if (hangar.stack.name === "reference") {
    const tenantUtilization = calculateUtilization(
      hangar,
      polygons,
      false,
      false // ignoreDeadSpaceForUtilization
    );
    const sqftValue = fbo.nightly_base_rate ?? 0.2;
    const value =
      (tenantUtilization / 100) *
      hangar.width *
      hangar.depth *
      sqftValue *
      (monthly ? 30.44 : 1);
    return formatCurrency(10 * Math.round(Math.ceil(value) / 10), 0);
  }
  const transientUtilization = calculateUtilization(
    {
      ...hangar,
      stack: {
        ...hangar.stack,
        tenants: hangar.stack.tenants.filter((t) => t.type === "transient"),
      },
    },
    polygons,
    true,
    false // ignoreDeadSpaceForUtilization
  );
  const sqftValue = fbo.nightly_transient_rate ?? 0.2;
  const value =
    (monthly ? 30.44 : 1) * hangar.base_tenant_value +
    (transientUtilization / 100) *
      hangar.width *
      hangar.depth *
      sqftValue *
      (monthly ? 30.44 : 1);
  return formatCurrency(10 * Math.round(Math.ceil(value) / 10), 0);
};

const isLocked = (t: Tenant): boolean => {
  return (
    t.position.preferences.find((p) => p === Preference.LOCKED) !== undefined
  );
};

export const calcEstimateTowTime = (hangar, polygons: any): string => {
  const tenants = findTenantsFullyInHangar(hangar, polygons).filter(
    (t) => !isLocked(t)
  );
  const dist =
    sum(
      tenants.map((t) =>
        Math.max(
          0,
          Math.abs(t.position.angle) > 90
            ? t.position.y ?? 0
            : t.aircraft.length + (t.position.y ?? 0)
        )
      )
    ) ?? 0;
  const totalMinutes = (2 * dist) / 150 + 6 * tenants.length;
  if (!totalMinutes) {
    return "–––";
  }
  return `${Math.ceil(totalMinutes)} min`;
};

export const HangarsPresenter: React.FC<Props> = () => {
  const { airplxIdentity } = useIdentity();
  const { activeFBO } = useActiveFBOs();
  const { hasPermission } = usePermissions();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("lg")); // Hide columns on tablets and smaller
  const displayEstimatedValue = getRoleSetting(
    activeFBO,
    "displayEstimatedValue",
    true
  );

  const displayEditTenants = hasPermission(Permission.MODIFY_TENANTS);

  const { ready, hangars } = useHangarsState();

  const sortedHangars = [...hangars].sort((a, b) =>
    sortHangars(a, b, activeFBO?.settings?.hangarDisplayOrder)
  );

  return (
    <Box
      sx={{
        ml: 2,
        mr: 2,
        height: "100%",
        minHeight: "calc(100vh - 64px)",
        pb: 4,
      }}
    >
      {!ready ? (
        <HangarsSkeleton />
      ) : (
        <Stack
          direction="column"
          sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          {/* Header with title and multi-hangar selector */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              mt: 2,
              mb: 2.25,
              pb: 1.5,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="h6">Hangars – {activeFBO?.name}</Typography>
            <MultiHangarButton hangars={hangars} />
          </Stack>

          {/* Stat Cards */}
          <Stack
            direction="row"
            spacing={2}
            sx={{
              mb: 2,
              width: "100%",
              maxWidth: 900,
              "& > *": {
                flex: 1,
                maxWidth: 420,
              },
            }}
          >
            <StatCard
              icon={HangarIcon}
              label="Total Hangars"
              value={hangars.length}
              testId="total_hangars"
            />
            <StatCard
              icon={FlightIcon}
              label="Total Aircraft in Hangars"
              value={sum(hangars.map((h) => h.stack.tenants.length))}
              testId="total_aircraft"
            />
          </Stack>

          {/* Hangar List */}
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              minHeight: 0,
              bgcolor: "background.paper",
              ...scrollSx,
            }}
          >
            {/* Header Row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: isTablet
                  ? "200px 160px 180px 120px 1fr"
                  : displayEstimatedValue
                  ? "200px 160px 180px 120px 140px 120px 1fr"
                  : "200px 160px 180px 140px 120px 1fr",
                alignItems: "center",
                gap: 2,
                px: 2.5,
                py: 1,
                borderBottom: "1px solid",
                borderColor: "grey.200",
                position: "sticky",
                top: 0,
                zIndex: 1,
                bgcolor: "background.paper",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500, fontSize: "0.95rem" }}
              >
                Hangar Name
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                justifyContent="flex-end"
              >
                <AirplanemodeActiveIcon
                  sx={{
                    color: "text.disabled",
                    fontSize: "0.85rem",
                    opacity: 0.8,
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500, fontSize: "0.95rem" }}
                >
                  Aircraft
                </Typography>
                <Tooltip title="# aircraft inside hangar / # aircraft wanted inside">
                  <InfoIcon
                    sx={{
                      color: "text.disabled",
                      fontSize: "0.85rem",
                      opacity: 0.8,
                    }}
                  />
                </Tooltip>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <SpeedIcon
                  sx={{
                    color: "text.disabled",
                    fontSize: "0.85rem",
                    opacity: 0.8,
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500, fontSize: "0.95rem" }}
                >
                  Utilization
                </Typography>
                <Tooltip
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Utilization
                      </Typography>
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{ mb: 1 }}
                      >
                        <strong>Stacked</strong> shows current utilization,
                        which can exceed 100% when aircraft are parked in the
                        negative space of other aircraft footprints.
                      </Typography>
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{ mb: 1 }}
                      >
                        <strong>At full tenant load</strong> shows what
                        utilization would be if every aircraft assigned to this
                        hangar were on the ground at once.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        The gauge shows a baseline marker at 100% - blue below,
                        green above.
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ display: "block", opacity: 0.8 }}
                      >
                        Adjust how this is calculated in Settings →
                        Configuration
                      </Typography>
                    </Box>
                  }
                  arrow
                >
                  <InfoIcon
                    sx={{
                      color: "text.disabled",
                      fontSize: "0.85rem",
                      opacity: 0.8,
                    }}
                  />
                </Tooltip>
              </Stack>
              {!isTablet && displayEstimatedValue && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  justifyContent="flex-end"
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500, fontSize: "0.95rem" }}
                  >
                    Est. Value
                  </Typography>
                  <Tooltip title="Estimated value of the hangar per night based on aircraft fully inside the hangar">
                    <InfoIcon
                      sx={{
                        color: "text.disabled",
                        fontSize: "0.85rem",
                        opacity: 0.8,
                      }}
                    />
                  </Tooltip>
                </Stack>
              )}
              {!isTablet && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  justifyContent="flex-end"
                >
                  <AccessTimeIcon
                    sx={{
                      color: "text.disabled",
                      fontSize: "0.85rem",
                      opacity: 0.8,
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 500, fontSize: "0.95rem" }}
                  >
                    Est. Tow Time
                  </Typography>
                  <Tooltip title="Estimated time to tow hangar into position">
                    <InfoIcon
                      sx={{
                        color: "text.disabled",
                        fontSize: "0.85rem",
                        opacity: 0.8,
                      }}
                    />
                  </Tooltip>
                </Stack>
              )}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <HistoryIcon
                  sx={{
                    color: "text.disabled",
                    fontSize: "0.85rem",
                    opacity: 0.8,
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500, fontSize: "0.95rem" }}
                >
                  Last Stacked
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="flex-end">
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500, fontSize: "0.95rem" }}
                >
                  Actions
                </Typography>
              </Stack>
            </Box>

            {/* Hangar Rows */}
            {sortedHangars.length === 0 ? (
              <EmptyState
                title="No Hangars Yet"
                description="Create your first hangar to start managing aircraft storage and stacking operations."
                icon={<HangarIcon sx={{ fontSize: 64, opacity: 0.5 }} />}
              />
            ) : (
              sortedHangars.map((hangar) => {
                const polygons = getStackPolygons(
                  hangar.stack,
                  hangar.width,
                  1
                );
                const nTenantsFullyInHangar = findTenantsFullyInHangar(
                  hangar,
                  polygons
                ).length;
                const utilizationStacked = calculateUtilization(
                  hangar,
                  polygons,
                  true,
                  activeFBO?.settings?.ignoreDeadSpaceForUtilization ?? false
                );
                const totalUtilization = sum(
                  hangar.stack.tenants.map(
                    ({ aircraft }) => aircraft.wingspan * aircraft.length
                  )
                );
                const utilizationAll = Math.round(
                  (100 * totalUtilization) /
                    calculateTotalArea(
                      hangar,
                      activeFBO?.settings?.ignoreDeadSpaceForUtilization ??
                        false
                    )
                );

                return (
                  <HangarRow
                    key={hangar.id}
                    hangar={hangar}
                    aircraft={`${nTenantsFullyInHangar} / ${
                      hangar.stack.tenants.length || 0
                    }`}
                    aircraftInside={nTenantsFullyInHangar}
                    aircraftTotal={hangar.stack.tenants.length || 0}
                    utilization={`${utilizationStacked}% / ${utilizationAll}%`}
                    utilizationStacked={utilizationStacked}
                    utilizationAll={utilizationAll}
                    estimatedValue={
                      displayEstimatedValue
                        ? estimateValue(
                            activeFBO,
                            hangar,
                            polygons,
                            activeFBO?.settings
                              ?.ignoreDeadSpaceForUtilization ?? false
                          )
                        : undefined
                    }
                    estimatedTowTime={calcEstimateTowTime(hangar, polygons)}
                    lastStacked={
                      hangar.last_stacked ? new Date(hangar.last_stacked) : null
                    }
                    displayEditTenants={displayEditTenants}
                    enableScheduling={
                      activeFBO?.settings?.enableScheduling ?? false
                    }
                    isTablet={isTablet}
                    displayEstimatedValue={displayEstimatedValue}
                  />
                );
              })
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
};
